import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Users, Heart, CheckCircle2, MousePointerClick, AlertCircle, TrendingUp, Clock, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import api from '../../lib/api';
import { toast } from 'sonner';

const StatCard = ({ title, value, icon: Icon, description, trend, trendValue }) => (
    <Card>
        <CardContent className="p-6">
            <div className="flex items-center justify-between space-y-0 pb-2">
                <p className="text-sm font-medium text-muted-foreground">{title}</p>
                <div className="h-10 w-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <Icon className="h-5 w-5 text-wedding-primary" />
                </div>
            </div>
            <div className="mt-4">
                <h3 className="text-2xl font-bold">{value}</h3>
                <p className="text-xs text-muted-foreground mt-1">{description}</p>
                {trend && (
                    <div className="flex items-center mt-4 text-xs font-medium text-green-600">
                        <TrendingUp className="h-4 w-4 mr-1" />
                        {trendValue}
                    </div>
                )}
            </div>
        </CardContent>
    </Card>
);

const AdminDashboard = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const response = await api.get('/admin/stats');
            setStats(response.data);
        } catch (error) {
            toast.error('Error al cargar métricas');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="animate-pulse space-y-8">
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                        {[1, 2, 3, 4].map(i => <div key={i} className="h-32 bg-slate-200 rounded-xl"></div>)}
                    </div>
                </div>
            </AdminLayout>
        );
    }

    const kpis = stats?.kpis || {};
    const alerts = stats?.alerts || {};

    return (
        <AdminLayout>
            <div className="space-y-8">
                {/* KPI Grid */}
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    <StatCard
                        title="Usuarios Totales"
                        value={kpis.total_users}
                        icon={Users}
                        description="Novios registrados"
                        trend={true}
                        trendValue="+12% vs mes pasado"
                    />
                    <StatCard
                        title="Bodas Activas"
                        value={kpis.total_weddings}
                        icon={Heart}
                        description="Eventos creados"
                    />
                    <StatCard
                        title="RSVPs Confirmados"
                        value={kpis.total_confirmations}
                        icon={CheckCircle2}
                        description={`${kpis.total_invitations} invitaciones enviadas`}
                    />
                    <StatCard
                        title="Conversión RSVP"
                        value={`${kpis.conversion_rsvp}%`}
                        icon={MousePointerClick}
                        description="Promedio de respuesta"
                    />
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                    {/* Alerts & Critical Info */}
                    <Card className="border-l-4 border-l-red-500">
                        <CardHeader>
                            <CardTitle className="flex items-center text-red-600">
                                <AlertCircle className="h-5 w-5 mr-2" />
                                Alertas Críticas
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                                <div className="flex items-center">
                                    <Clock className="h-5 w-5 text-red-500 mr-3" />
                                    <div>
                                        <p className="font-semibold text-red-900">Suscripciones Vencidas</p>
                                        <p className="text-sm text-red-700">{alerts.expired_subscriptions} usuarios requieren renovación</p>
                                    </div>
                                </div>
                                <Button variant="outline" size="sm" className="bg-white border-red-200 text-red-700 hover:bg-red-50">Gestionar</Button>
                            </div>
                            {/* More alerts here */}
                        </CardContent>
                    </Card>

                    {/* Upcoming Weddings */}
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle>Próximas Bodas (7 días)</CardTitle>
                            <Button variant="link" size="sm" className="text-wedding-primary">Ver todas</Button>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {alerts.upcoming_weddings?.length > 0 ? (
                                    alerts.upcoming_weddings.map((wedding) => (
                                        <div key={wedding.id} className="flex items-center justify-between border-b pb-4 last:border-0 last:pb-0">
                                            <div>
                                                <p className="font-medium text-slate-800">{wedding.title}</p>
                                                <p className="text-xs text-slate-500">{new Date(wedding.event_date).toLocaleDateString()}</p>
                                            </div>
                                            <Button variant="outline" size="sm" onClick={() => navigate(`/admin/weddings/${wedding.id}`)}>
                                                Detalle
                                            </Button>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-sm text-muted-foreground py-4 text-center">No hay bodas próximas</p>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Quick Actions */}
                <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-3">
                    <Card className="bg-wedding-primary text-white">
                        <CardContent className="p-6">
                            <h3 className="text-lg font-bold mb-2">Gestión de Usuarios</h3>
                            <p className="text-sm text-white/80 mb-4">Administra cuentas de novios, activa suscripciones y resuelve problemas de acceso.</p>
                            <Button variant="secondary" className="w-full text-wedding-primary" onClick={() => window.location.href = '/admin/users'}>
                                Ir a Usuarios <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        </CardContent>
                    </Card>

                    <Card className="bg-slate-800 text-white">
                        <CardContent className="p-6">
                            <h3 className="text-lg font-bold mb-2">Planes y Precios</h3>
                            <p className="text-sm text-white/80 mb-4">Modifica los límites de invitados, precios y funciones de los paquetes disponibles.</p>
                            <Button variant="secondary" className="w-full text-slate-800" onClick={() => window.location.href = '/admin/plans'}>
                                Ver Planes <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        </CardContent>
                    </Card>

                    <Card className="bg-slate-100 border-slate-200">
                        <CardContent className="p-6 text-slate-800">
                            <h3 className="text-lg font-bold mb-2">Configuración Global</h3>
                            <p className="text-sm text-slate-600 mb-4">Ajusta variables del sistema, modo mantenimiento y URLs de conexión.</p>
                            <Button variant="outline" className="w-full border-slate-300" onClick={() => window.location.href = '/admin/settings'}>
                                Ajustes <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </AdminLayout>
    );
};

export default AdminDashboard;
